import { createNotification } from "react-redux-notify";
import { all, put, select, takeLatest } from "redux-saga/effects";
import { getErrorNotificationMessage, getSuccessNotificationMessage } from "../../components/helper/NotificationMessage";
import {
  FETCH_TRACKING_LINKS_START,
  CREATE_TRACKING_LINK_START,
  UPDATE_TRACKING_LINK_START,
  DELETE_TRACKING_LINK_START,
  TRACK_VIEW_TRACKING_LINK_START
} from "../actions/ActionConstant";
import { hideErrorComponent, showErrorComponent } from "../actions/ComponentAction";
import { parseContentServiceError } from "../../helpers/services/content/error";
import trackingLinksService from "../../helpers/services/content/tracking-links.service";
import {
  fetchTrackingLinksSuccess,
  fetchTrackingLinksFailure,
  createTrackingLinkSuccess,
  createTrackingLinkFailure,
  updateTrackingLinkSuccess,
  updateTrackingLinkFailure,
  deleteTrackingLinkSuccess,
  deleteTrackingLinkFailure,
  trackViewTrackingLinkSuccess,
  trackViewTrackingLinkFailure
} from "../actions/TrackingLinkAction";

function* fetchTrackingLinks(action) {
  try {
    const { page = 1, limit = 10 } = action.payload || {};
    const response = yield trackingLinksService.getTrackingLinks(page, limit);
    if (response.data) {
      yield put(fetchTrackingLinksSuccess(response));
    } else {
      yield put(fetchTrackingLinksFailure(response));
    }
    yield put(hideErrorComponent());
  } catch (error) {
    yield put(fetchTrackingLinksFailure(error));
    if (error?.response?.status !== 400) {
      yield put(showErrorComponent({ error: error, status: error?.response?.status || 500 }));
    }
    const notificationMessage = getErrorNotificationMessage(parseContentServiceError(error.response));
    yield put(createNotification(notificationMessage));
  }
}

function* createTrackingLink(action) {
  try {
    const response = yield trackingLinksService.createTrackingLink(action.payload);
    if (response) {
      yield put(createTrackingLinkSuccess(response));
      const notificationMessage = getSuccessNotificationMessage("Tracking link created successfully");
      yield put(createNotification(notificationMessage));
      yield put(hideErrorComponent());
      // Refresh the list from page 1 so pagination resets correctly
      const currentState = yield select((state) => state.trackingLinks.trackingLinks);
      const metadata = currentState.metadata || {};
      const limit = metadata.perPage || metadata.limit || 10;
      yield put({ type: FETCH_TRACKING_LINKS_START, payload: { page: 1, limit } });
    } else {
      yield put(createTrackingLinkFailure(response));
    }
  } catch (error) {
    yield put(createTrackingLinkFailure(error));
    if (error?.response?.status !== 400) {
      yield put(showErrorComponent({ error: error, status: error?.response?.status || 500 }));
    }
    const notificationMessage = getErrorNotificationMessage(parseContentServiceError(error.response));
    yield put(createNotification(notificationMessage));
  }
}

function* updateTrackingLink(action) {
  try {
    const { id, data } = action.payload;
    const response = yield trackingLinksService.updateTrackingLink(id, data);
    if (response) {
      yield put(updateTrackingLinkSuccess(response));
      const notificationMessage = getSuccessNotificationMessage("Tracking link updated successfully");
      yield put(createNotification(notificationMessage));
      yield put(hideErrorComponent());
      // Refresh the list - stay on current page
      const currentState = yield select((state) => state.trackingLinks.trackingLinks);
      const metadata = currentState.metadata || {};
      const currentPage = metadata.page || 1;
      const limit = metadata.perPage || metadata.limit || 10;
      yield put({ type: FETCH_TRACKING_LINKS_START, payload: { page: currentPage, limit } });
    } else {
      yield put(updateTrackingLinkFailure(response));
    }
  } catch (error) {
    yield put(updateTrackingLinkFailure(error));
    if (error?.response?.status !== 400) {
      yield put(showErrorComponent({ error: error, status: error?.response?.status || 500 }));
    }
    const notificationMessage = getErrorNotificationMessage(parseContentServiceError(error.response));
    yield put(createNotification(notificationMessage));
  }
}

function* deleteTrackingLink(action) {
  try {
    const response = yield trackingLinksService.deleteTrackingLink(action.payload);
    if (response.success) {
      yield put(deleteTrackingLinkSuccess(action.payload));
      const notificationMessage = getSuccessNotificationMessage(
        response.message || "Tracking link deleted successfully"
      );
      yield put(createNotification(notificationMessage));
      yield put(hideErrorComponent());
      // Refresh the list from page 1 after deletion to keep pagination consistent
      const currentState = yield select((state) => state.trackingLinks.trackingLinks);
      const metadata = currentState.metadata || {};
      const limit = metadata.perPage || metadata.limit || 10;
      yield put({ type: FETCH_TRACKING_LINKS_START, payload: { page: 1, limit } });
    } else {
      yield put(deleteTrackingLinkFailure(response.data?.error || "Failed to delete tracking link"));
      const notificationMessage = getErrorNotificationMessage(
        response.data?.error || "Failed to delete tracking link"
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(deleteTrackingLinkFailure(error));
    const notificationMessage = getErrorNotificationMessage(parseContentServiceError(error.response));
    yield put(createNotification(notificationMessage));
  }
}

function* trackViewTrackingLink(action) {
  try {
    const response = yield trackingLinksService.trackView(action.payload);
    if (response.success) {
      yield put(trackViewTrackingLinkSuccess(response));
    } else {
      yield put(trackViewTrackingLinkFailure(response));
    }
  } catch (error) {
    yield put(trackViewTrackingLinkFailure(error));
  }
}

export default function* trackingLinkSaga() {
  yield all([
    takeLatest(FETCH_TRACKING_LINKS_START, fetchTrackingLinks),
    takeLatest(CREATE_TRACKING_LINK_START, createTrackingLink),
    takeLatest(UPDATE_TRACKING_LINK_START, updateTrackingLink),
    takeLatest(DELETE_TRACKING_LINK_START, deleteTrackingLink),
    takeLatest(TRACK_VIEW_TRACKING_LINK_START, trackViewTrackingLink)
  ]);
}

